import { FEATURE_GATES } from 'pb-billing-ui/feature-gates.ts';
import type { ScanDepth } from '~/types/person/footprint';

/** Shared scan levels keep the ladder, action bar and upgrade destination consistent. */
export interface JourneyLevel {
    level: number;
    depth: ScanDepth;
    name: string;
    title: string;
    pitch: string;
    truth: string;
    /** Shown on a locked rung. Level 2 is any paid plan; level 3 is Signature alone. */
    plan: string | null;
    /**
     * What a locked level is waiting on, said where the control stands. Null on a level
     * nothing can lock.
     */
    requirement: string | null;
    /** Shared checkout gate for a level that requires a specific plan. */
    upgradeFeature?: string;
}

const LADDER: readonly Omit<JourneyLevel, 'title'>[] = [
    {
        level: 1,
        depth: 'quick',
        name: 'Surface',
        pitch: 'Direct detection across every source we hold. There is no intelligence layer on top of it: a record surfaces because it matches something you have verified, never because we inferred a connection. This is where almost every other service stops, including the premium ones.',
        truth: 'Every record that comes back is broken into fields, collapsed where it repeats, and counted across every source it appears in. Nothing is inferred, and nothing is scored.',
        plan: null,
        requirement: null,
    },
    {
        level: 2,
        depth: 'standard',
        name: 'Thorough',
        pitch: 'The intelligence layer. We take what the surface pass returned and cross-reference it, triangulating separate data points to reach records a direct search cannot.',
        truth: 'It works from what the first level found, which is why that level has to be sorted before this one opens.',
        plan: 'any paid plan',
        requirement: 'Paid license required',
    },
    {
        level: 3,
        depth: 'deep',
        name: 'Deep',
        pitch: 'The same correlation engine, amplified across everything the first two levels turned up, until there is nothing left to turn over.',
        truth: 'The deepest we go, and every pass above it feeds it, so it is only as good as the sorting you did on the way down.',
        plan: 'Signature',
        requirement: 'Signature license required',
        upgradeFeature: FEATURE_GATES['deep-scan'].key,
    },
];

/**
 * The ladder as everything else reads it. A heading is the level's name plus the thing it
 * names, computed here rather than typed a second time beside the name it must agree with.
 */
export const JOURNEY_LEVELS: readonly JourneyLevel[] = LADDER.map((level) => ({
    ...level,
    title: `${level.name} Scan`,
}));

/** Where a scan depth sits on the ladder. */
export const LEVEL_OF_DEPTH: Record<ScanDepth, number> = {
    quick: 1,
    standard: 2,
    deep: 3,
};
